import React, { useEffect, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Avatar from 'ui-component/extended/Avatar';
import { IconUserCheck } from '@tabler/icons-react';
import { useCreateUserStore } from 'Application/store';
import Lottie from 'react-lottie';
import SuccessAnime from 'assets/lottie/waiting.json';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockTwoToneIcon from '@mui/icons-material/BlockTwoTone';
import EditIcon from '@mui/icons-material/Edit';
import Loader from 'ui-component/Loader';
import NoResultsFound from 'Application/components/NoResultsFound';
import { ManagedUser, UpdateUser, UserRole, UsersManagementProps } from 'Application/models';
import EditUser from 'Application/components/Modals/EditUser';
import AppDialog from 'Application/components/Modals/AppDialog';
import { toast } from 'sonner';
import { FormattedRoleName, getUserRoleNameFromStorage, removeNameRole } from 'Application/utils';

interface UsersListProps {
    usersManagment: UsersManagementProps;
}

// const roleMapping: Record<string, string> = {
//     ROLE_AGENT: 'Agent',
//     ROLE_AGENCY_ADMIN: 'Agency Admin'
//     // Add more role mappings as needed
// };

interface UserStatus {
    name: string;
    email: string;
    locked: boolean;
}

const UsersList: React.FC<UsersListProps> = ({ usersManagment }) => {
    const { searchTerm, rowsPerPage, currentPage, countryValue, searchInput, setSearchInput, setCurrentPage } = usersManagment;

    const theme = useTheme();
    const {
        getInternalUsers,
        agencyUserData,
        activateUser,
        deactivateUser,
        activateUserData,
        deactivateUserData,
        isLoading,
        isError,
        error
    } = useCreateUserStore();

    const [open, setOpen] = React.useState(false);
    const [userStatus, setUserStatus] = React.useState<UserStatus | null>(null);
    const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UpdateUser | null>(null);

    const userRole = getUserRoleNameFromStorage();

    useEffect(() => {
        getInternalUsers(searchTerm, currentPage, rowsPerPage, countryValue);
    }, [searchTerm, currentPage, rowsPerPage, countryValue]);

    if (isLoading && !(open || isEditUserDialogOpen)) {
        return <Loader />;
    }

    if (isError) {
        return <Alert severity="error">Error returning the users list. Please try again later.</Alert>;
    }

    if (!isLoading && agencyUserData?.content?.length === 0) {
        return <NoResultsFound title="No results found" />;
    }

    const defaultOptions = {
        loop: true,
        autoplay: true,
        animationData: SuccessAnime,
        rendererSettings: {
            preserveAspectRatio: 'xMidYMid slice'
        }
    };

    const formatRoles = (roleString: string) => {
        const formattedRole = FormattedRoleName(roleString);
        return removeNameRole(formattedRole);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = (user: UserStatus) => {
        setOpen(true);
        setUserStatus(user);
    };

    const handleConfirm = async () => {
        if (!userStatus) {
            handleClose();
            return;
        }

        if (userStatus.locked) {
            await activateUser(userStatus.email);
            searchInput !== ''
                ? setSearchInput('')
                : currentPage !== 1
                  ? setCurrentPage(1)
                  : getInternalUsers(searchTerm, currentPage, rowsPerPage, countryValue);
            // await getInternalUsers(); // Refresh users list
            // TODO: Display toast messages when user is activated/deactivated successfully
            toast.success('User activated successfully');
        } else {
            await deactivateUser(userStatus.email);
            searchInput !== ''
                ? setSearchInput('')
                : currentPage !== 1
                  ? setCurrentPage(1)
                  : getInternalUsers(searchTerm, currentPage, rowsPerPage, countryValue);
            // await getInternalUsers(); // Refresh users list
            toast.success('User deactivated successfully');
        }

        handleClose();
    };

    const handleEditUser = (user: ManagedUser) => {
        const updatedUser: UpdateUser = {
            firstName: user.firstName,
            otherNames: user.lastName,
            emailAddress: user.emailAddress,
            regionName: user.region?.name,
            id: user.id,
            role: user.role.roleName as UserRole
        };
        setSelectedUser(updatedUser);
        setIsEditUserDialogOpen(true);
    };

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ pl: 3 }}>Name</TableCell>
                        <TableCell>Role</TableCell>
                        {userRole === UserRole.ROLE_ADMINISTRATOR && <TableCell>Country</TableCell>}
                        <TableCell>Date Created</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center" sx={{ pr: 3 }}>
                            Actions
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {agencyUserData?.content?.map((user, index) => {
                        const firstNameInitial = user.firstName ? user.firstName.charAt(0).toUpperCase() : '?';
                        const lastNameInitial = user.lastName ? user.lastName.charAt(0).toUpperCase() : '?';

                        return (
                            <TableRow hover key={index}>
                                <TableCell sx={{ pl: 3 }}>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Avatar sx={{ fontSize: '0.9rem' }} alt={`${user.firstName || 'N/A'} ${user.lastName || 'N/A'}`}>
                                            {firstNameInitial}
                                            {lastNameInitial}
                                        </Avatar>
                                        <Stack>
                                            <Stack direction="row" alignItems="center" spacing={0.25}>
                                                <Typography variant="subtitle1">
                                                    {user.firstName} {user.lastName}
                                                </Typography>
                                                {!user.locked && <CheckCircleIcon sx={{ color: 'success.dark', width: 14, height: 14 }} />}
                                            </Stack>
                                            <Typography variant="subtitle2" noWrap>
                                                {user.emailAddress}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </TableCell>
                                <TableCell>{formatRoles(user.role.roleName)}</TableCell>
                                {userRole === UserRole.ROLE_ADMINISTRATOR && <TableCell>{user.region?.name}</TableCell>}
                                <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                                <TableCell>
                                    {!user.locked ? (
                                        <Chip
                                            label="Active"
                                            size="small"
                                            sx={{
                                                bgcolor:
                                                    theme.palette.mode === 'dark' ? 'dark.main' : alpha(theme.palette.success.light, 0.6),
                                                color: 'success.dark'
                                            }}
                                        />
                                    ) : (
                                        <Chip
                                            label="Inactive"
                                            size="small"
                                            sx={{
                                                bgcolor:
                                                    theme.palette.mode === 'dark' ? 'dark.main' : alpha(theme.palette.orange.light, 0.8),
                                                color: 'orange.dark'
                                            }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="center" sx={{ pr: 3 }}>
                                    <Stack direction="row" justifyContent="center" alignItems="center">
                                        <Tooltip placement="top" title="Edit" onClick={() => handleEditUser(user)}>
                                            <IconButton color="primary" aria-label="edit" size="large">
                                                <EditIcon sx={{ fontSize: '1.1rem' }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                            placement="top"
                                            title={user.locked ? 'Activate' : 'Deactivate'}
                                            onClick={() =>
                                                handleOpen({
                                                    name: `${user.firstName} ${user.lastName}`,
                                                    email: user.emailAddress,
                                                    locked: user.locked
                                                })
                                            }
                                        >
                                            <IconButton
                                                color="primary"
                                                sx={
                                                    user.locked
                                                        ? {
                                                              color: 'success.dark',
                                                              borderColor: 'success.main',
                                                              '&:hover ': { bgcolor: 'success.light' }
                                                          }
                                                        : {
                                                              color: 'orange.dark',
                                                              borderColor: 'orange.main',
                                                              '&:hover ': { bgcolor: 'orange.light' }
                                                          }
                                                }
                                                size="large"
                                            >
                                                {user.locked ? (
                                                    <IconUserCheck stroke={2} size={20} />
                                                ) : (
                                                    <BlockTwoToneIcon sx={{ fontSize: '1.1rem' }} />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            {userStatus && (
                <AppDialog
                    open={open}
                    title={userStatus.locked ? 'Activate User' : 'Deactivate User'}
                    description={`Are you sure you want to ${userStatus.locked ? 'activate' : 'deactivate'} ${userStatus.name}?`}
                    isLoading={isLoading}
                    loadingButtonTitle={userStatus.locked ? 'Activating...' : 'Deactivating...'}
                    Graphic={<Lottie options={defaultOptions} height={100} width={100} />}
                    handleClose={handleClose}
                    handleConfirm={handleConfirm}
                />
            )}

            {selectedUser && (
                <EditUser
                    isOpen={isEditUserDialogOpen}
                    setIsOpen={setIsEditUserDialogOpen}
                    user={selectedUser}
                    usersManagment={usersManagment}
                />
            )}
        </TableContainer>
    );
};

export default UsersList;
